import { Formik, Field, Form as FormikForm, ErrorMessage, type FormikHelpers } from "formik"
import * as Yup from "yup"
import {
  PageContainer,
  VisualSection,
  FormSection,
  Title,
  InputGroup,
  Label,
  Input,
  Error,
  SubmitButton,
  PresetButtonGroup,
  PresetButton,
  Logo,
  GridRow,
  TextArea,
  Logo2,
  SuccessActions,
  PrimaryLinkButton,
  SecondaryLinkButton,
  FormEyebrow,
  FormIntro,
  TrustList,
  DirectContact,
} from "./Style"
import SvgLogo from "../../../app/assets/img/logo.svg"
import { useState } from "react"
import missionImage from "@assets/img/mission-image.jpg"
import api from "@utils/api.client"
import { trackEvent } from "@components/utils/tracking.client"

const initialValues = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  legalEntity: "",
  numBuildings: "",
  numUnits: "",
  sector: "",
  additionalInfo: "",
}

const validationSchema = Yup.object({
  firstName: Yup.string().required("Prénom est requis."),
  lastName: Yup.string().required("Nom est requis."),
  email: Yup.string().email("Adresse courriel invalide.").required("Courriel est requis."),
  phone: Yup.string()
    .matches(/^\D*(\d\D*){10}$/, "Numéro de téléphone invalide (10 chiffres requis).")
    .required("Téléphone est requis."),
  numBuildings: Yup.number().required("Nombre d'immeubles est requis."),
  numUnits: Yup.number().required("Nombre de portes est requis."),
  sector: Yup.string().required("Secteur est requis."),
})

const RequestContactForm = () => {
  const [presetUnits, setPresetUnits] = useState<number | null>(null)
  const [successMessage, setSuccessMessage] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState("")

  const handlePresetClick = (value: number, setFieldValue: FormikHelpers<typeof initialValues>["setFieldValue"]) => {
    setPresetUnits(value)
    setFieldValue("numUnits", value)
  }

  const handleSubmit = async (values: typeof initialValues, { resetForm }: FormikHelpers<typeof initialValues>) => {
    if (isSubmitting) return // Prevent duplicate submissions

    setIsSubmitting(true) // Disable further submissions
    setSuccessMessage(false)
    setSubmitError("")
    try {
      const response = await api.post("/request-contact-form", {
        ...values,
        inquiryType: "soumission",
        phone: values.phone.replace(/\D/g, ""),
        numBuildings: Number(values.numBuildings),
        numUnits: Number(values.numUnits),
      })

      if (response.status === 200 || response.status === 201) {
        trackEvent("generate_lead", { form_name: "consultation_generale" })
        setSuccessMessage(true)
        resetForm()
      }
    } catch {
      setSubmitError("La demande n’a pas pu être envoyée. Réessayez ou appelez-nous directement.")
    } finally {
      setIsSubmitting(false) // Re-enable submissions
    }
  }

  if (successMessage) {
    return (
      <PageContainer>
        <VisualSection src={missionImage} alt="" />
        <Logo href="/" aria-label="Retour à l’accueil">
          <SvgLogo />
        </Logo>
        <FormSection>
          <FormEyebrow>Demande reçue</FormEyebrow>
          <Title>Merci. Le premier contexte est déjà clair.</Title>
          <FormIntro>
            Nous examinerons le territoire, le nombre de portes et les responsabilités que vous souhaitez déléguer avant de vous recontacter.
          </FormIntro>
          <SuccessActions>
            <PrimaryLinkButton href="/">Retour à l'accueil</PrimaryLinkButton>
            <SecondaryLinkButton href="/guide-proprietaires-portneuf">Consulter nos ressources</SecondaryLinkButton>
          </SuccessActions>
        </FormSection>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <VisualSection src={missionImage} alt="" />
      <Logo href="/" aria-label="Retour à l’accueil">
        <SvgLogo />
      </Logo>

      <FormSection>
        <FormEyebrow>Premier échange · sans engagement</FormEyebrow>
        <Title>Parlez-nous de vos immeubles.</Title>
        <FormIntro>
          Quelques renseignements suffisent pour vérifier si notre territoire, notre capacité et notre façon de travailler correspondent à votre
          besoin.
        </FormIntro>
        <TrustList>
          <li>Mandat défini selon vos priorités</li>
          <li>Équipe locale à Neuville</li>
          <li>Échange confidentiel</li>
        </TrustList>
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
          {({ setFieldValue }) => (
            <FormikForm>
              <GridRow columns="3fr 5fr">
                <InputGroup>
                  <Label htmlFor="firstName">Prénom</Label>
                  <Field as={Input} id="firstName" name="firstName" autoComplete="given-name" />
                  <ErrorMessage name="firstName" component={Error} />
                </InputGroup>
                <InputGroup>
                  <Label htmlFor="lastName">Nom</Label>
                  <Field as={Input} id="lastName" name="lastName" autoComplete="family-name" />
                  <ErrorMessage name="lastName" component={Error} />
                </InputGroup>
              </GridRow>

              <GridRow columns="5fr 3fr">
                <InputGroup>
                  <Label htmlFor="email">Courriel</Label>
                  <Field as={Input} id="email" name="email" type="email" autoComplete="email" />
                  <ErrorMessage name="email" component={Error} />
                </InputGroup>
                <InputGroup>
                  <Label htmlFor="phone">Téléphone</Label>
                  <Field as={Input} id="phone" name="phone" type="tel" inputMode="numeric" autoComplete="tel" />
                  <ErrorMessage name="phone" component={Error} />
                </InputGroup>
              </GridRow>

              <GridRow>
                <InputGroup>
                  <Label htmlFor="legalEntity">Entreprise ou entité (facultatif)</Label>
                  <Field as={Input} id="legalEntity" name="legalEntity" />
                </InputGroup>
                <InputGroup>
                  <Label htmlFor="sector">Ville ou secteur</Label>
                  <Field as={Input} id="sector" name="sector" placeholder="Ex. Donnacona" />
                  <ErrorMessage name="sector" component={Error} />
                </InputGroup>
              </GridRow>

              <GridRow>
                <InputGroup>
                  <Label htmlFor="numBuildings">Nombre d'immeubles</Label>
                  <Field as={Input} id="numBuildings" name="numBuildings" type="number" min="1" />
                  <ErrorMessage name="numBuildings" component={Error} />
                </InputGroup>
                <InputGroup>
                  <Label htmlFor="numUnits">Nombre de portes</Label>
                  <Field as={Input} id="numUnits" name="numUnits" type="number" min="1" />
                  <PresetButtonGroup>
                    {[2, 6, 12, 20, 30].map((value) => (
                      <PresetButton
                        key={value}
                        type="button"
                        $isActive={presetUnits === value}
                        onClick={() => handlePresetClick(value, setFieldValue)}
                      >
                        {value}
                      </PresetButton>
                    ))}
                  </PresetButtonGroup>
                  <ErrorMessage name="numUnits" component={Error} />
                </InputGroup>
              </GridRow>

              <InputGroup>
                <Label htmlFor="additionalInfo">Qu’aimeriez-vous déléguer? (facultatif)</Label>
                <Field
                  as={TextArea}
                  id="additionalInfo"
                  name="additionalInfo"
                  placeholder="Location, demandes des locataires, entretien, administration, travaux…"
                />
              </InputGroup>

              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Envoi en cours…" : "Demander un premier échange"}
              </SubmitButton>

              {submitError && <Error role="alert">{submitError}</Error>}
              <DirectContact>
                Vous préférez appeler? <a href="tel:+14186849004">(418) 684-9004</a>. Les renseignements sont utilisés uniquement pour répondre à
                votre demande.
              </DirectContact>

              <Logo2 href="/">
                <SvgLogo />
              </Logo2>
            </FormikForm>
          )}
        </Formik>
      </FormSection>
    </PageContainer>
  )
}

export default RequestContactForm
